import {
  ConditionExpression,
  ConditionStep,
} from "@redotech/redo-flows-common/steps";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import { DeepPartial, PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import { getConditionSummary } from "./condition-summary";
import { ConditionSummaryText } from "./condition-summary-text";
import { useConditionValueLabels } from "./condition-value-labels";
import * as NodeCSS from "./node.module.css";
import { useFlipBranchesMenuItems } from "./use-flip-branches-menu-items";

export interface BranchNodeData {
  step: PartialWithRequiredType<ConditionStep>;
  deleteElements: (id: string) => void;
  updateStep: (
    id: string,
    updatedStep: PartialWithRequiredType<ConditionStep>,
  ) => void;
  readonly: boolean;
  schema?: Schema;
}

export function BranchNode(data: {
  id: string;
  data: BranchNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const expression: DeepPartial<ConditionExpression> | undefined =
    data.data.step.expression;

  const valueLabels = useConditionValueLabels(expression);

  const summarySegments = useMemo(
    () => getConditionSummary(expression, valueLabels, data.data.schema),
    [expression, valueLabels, data.data.schema],
  );

  const additionalMenuItems = useFlipBranchesMenuItems({
    id: data.id,
    step: data.data.step,
    readonly: data.data.readonly,
    updateStep: data.data.updateStep,
  });

  return (
    <BaseNode
      additionalMenuItems={additionalMenuItems}
      icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
      onDelete={
        data.data.readonly ? undefined : () => data.data.deleteElements(data.id)
      }
      selected={data.selected}
      step={data.data.step}
      title={t("Branch")}
    >
      <Handle position={Position.Top} type="target" />
      <Flex className={NodeCSS.fullWidth} dir="column" justify="flex-start">
        <Text fontSize="xxs">
          {data.data.step.description ? t("Description") : t("Condition")}
        </Text>
        {data.data.step.description ? (
          <Text className={NodeCSS.valueText}>
            {data.data.step.description}
          </Text>
        ) : summarySegments.length ? (
          <ConditionSummaryText segments={summarySegments} />
        ) : (
          <Text className={NodeCSS.placeholderText}>
            {t("Needs configuration")}
          </Text>
        )}
      </Flex>
      <Handle id="a" position={Position.Bottom} type="source" />
      <Handle id="b" position={Position.Bottom} type="source" />
    </BaseNode>
  );
}
